import { useTranslation } from 'react-i18next';
import './Input.css'
import './OTPInput.css'
import { Dispatch, forwardRef, SetStateAction, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { InputInfoMsg } from './Input';
import Svgs from 'assets/svgs';
import { smoothScrollTo } from 'common/scroll';

interface OTPInputProps {
    inputVal: string,
    setInputVal: Dispatch<SetStateAction<string>>
    time?: number
    resendTime?: number
    onStart: () => void
    infoMsg?: InputInfoMsg
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onClear?: () => void
    showClearButton?: boolean
    readOnly?: boolean
}

export interface OTPInputRef {
    startCountDown: (skipOnStart?: boolean) => void;
    resetTimer: () => void;
    focus: () => void;
}

export const OTPInput = forwardRef<OTPInputRef, OTPInputProps>(({
    inputVal,
    setInputVal,
    time,
    resendTime,
    onStart,
    infoMsg,
    onChange,
    onClear,
    showClearButton = true,
    readOnly = false
}, ref) => {
    const {t} = useTranslation();
    const formatTime = (time: number) => {
        const min = (Math.floor(time / 60)).toString().padStart(2, '0');
        const sec = (time % 60).toString().padStart(2, '0');
        return `${min}:${sec}`
    }
    const inputRef = useRef<HTMLInputElement>(null);
    const timerTime = useRef(time ?? 180);
    const timerId = useRef<NodeJS.Timer | undefined>(undefined);
    const [leftTime, setLeftTime] = useState(formatTime(timerTime.current));
    const [isTimerActive, setIsTimerActive] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [btnDisabled, setBtnDisabled] = useState(true);

    const onStartCountDown = (skipOnStart = false) => {
        if (!skipOnStart) {
            onStart();
        }

        // 기존 타이머 정리
        if (timerId.current) {
            clearInterval(timerId.current);
            timerId.current = undefined;
        }

        // 타이머 초기화
        timerTime.current = time ?? 180;
        setLeftTime(formatTime(timerTime.current));
        setIsTimerActive(true);
        setBtnDisabled(true);

        timerId.current = setInterval(() => {
            timerTime.current -= 1;
            if(timerTime.current <= 0) {
                timerTime.current = 0;
                setIsTimerActive(false);
                clearInterval(timerId.current);
                timerId.current = undefined;
            } else if (timerTime.current < (resendTime ?? 60)) {
                setBtnDisabled(false);
            }
            setLeftTime(formatTime(timerTime.current));
        }, 1000);
    }

    // 컴포넌트 언마운트 시 타이머 정리
    useEffect(() => {
        return () => {
            if (timerId.current) {
                clearInterval(timerId.current);
                timerId.current = undefined;
            }
        };
    }, []);

    useImperativeHandle(ref, () => ({
        startCountDown(skipOnStart = false){
            onStartCountDown(skipOnStart);
        },
        resetTimer() {
            // 타이머 정리 및 초기화
            if (timerId.current) {
                clearInterval(timerId.current);
                timerId.current = undefined;
            }
            timerTime.current = time ?? 180;
            setLeftTime(formatTime(timerTime.current));
            setIsTimerActive(false);
            setBtnDisabled(true);
        },
        focus() {
            inputRef.current?.focus();
        }
    }))


    return (
        <div style={{padding: "10px 0"}}>
            <div className="common-input-title-container">
                <span className="common-input-title">{t("signUp.otp")}</span>
            </div>
            <div className="otp-input-box">
                <div className="otp-input-wrapper">
                    <input
                        ref={inputRef}
                        value={inputVal}
                        onFocus={() => {
                            const input = inputRef.current;
                            if (!input) return;

                            const vv = window.visualViewport;
                            let settled = false;
                            let debounceTimer: ReturnType<typeof setTimeout>;

                            const scrollToInput = () => {
                                if (settled) return;
                                settled = true;
                                vv?.removeEventListener('resize', onResize);

                                if (document.activeElement !== input) return;
                                requestAnimationFrame(() => {
                                    const container = input.closest('.otp-input-box')?.parentElement;
                                    const containerRect = (container || input).getBoundingClientRect();

                                    const headerEl = document.querySelector('.header_wrapper') as HTMLElement;
                                    const headerBottom = headerEl ? headerEl.getBoundingClientRect().bottom : 0;

                                    const stepEl = input.closest('[class^="sign-up-step"]');
                                    const bottomBtnEl = stepEl?.querySelector(':scope > div:last-child') as HTMLElement;
                                    const visibleHeight = vv?.height ?? window.innerHeight;
                                    const visibleBottom = visibleHeight - (bottomBtnEl?.offsetHeight ?? 0);

                                    if (containerRect.top < headerBottom) {
                                        smoothScrollTo(Math.max(0, window.scrollY + containerRect.top - headerBottom));
                                    } else if (containerRect.bottom > visibleBottom) {
                                        smoothScrollTo(window.scrollY + containerRect.bottom - visibleBottom);
                                    }
                                });
                            };

                            // 키보드 resize가 멈출 때까지 디바운스 후 스크롤
                            const onResize = () => {
                                clearTimeout(debounceTimer);
                                debounceTimer = setTimeout(scrollToInput, 150);
                            };
                            vv?.addEventListener('resize', onResize);
                            // Fallback: 키보드가 이미 열려있어 resize가 발생하지 않는 경우
                            setTimeout(scrollToInput, 300);
                        }}
                        onChange={(e) => {
                            setInputVal(e.target.value.replace(/[^0-9]/g, ''))
                            onChange(e)
                        }}
                        className="otp-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        readOnly={readOnly}
                        autoComplete="one-time-code"
                    />
                    {showClearButton && inputVal && !readOnly && (
                        <button
                            type="button"
                            className="otp-input-clear-btn"
                            onClick={() => {
                                setInputVal('');
                                onClear?.();
                            }}
                            tabIndex={-1}
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <circle cx="10" cy="10" r="10" fill="var(--lightGray5)"/>
                                <path d="M6.5 6.5L13.5 13.5M13.5 6.5L6.5 13.5" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </button>
                    )}
                </div>
                <button
                    disabled={(isTimerActive && btnDisabled) || isResending}
                    className="otp-input-resend"
                    onClick={() => {
                        setIsResending(true);
                        onStart();
                        setTimeout(() => setIsResending(false), 1000);
                    }}
                >
                    {t("signUp.otpResend")}
                </button>
            </div>
            <div className="otp-input-msg-wrapper">
                <span style={{display: "flex", alignItems: "center", gap: "2px"}}>
                    {infoMsg &&
                        <>
                            {infoMsg.type === "error" &&
                                <Svgs.IcError
                                    height="12px"
                                    width="12px"
                                    color="var(--warning)"
                                />
                            }
                            {infoMsg.type === "passed" &&
                                <Svgs.IcPassed
                                    height="12px"
                                    width="12px"
                                    color="var(--primary)"
                                />
                            }
                            <span className={`common-input-msg ${infoMsg.type}`}>{infoMsg?.msg}</span>
                        </>
                    }

                </span>

                {isTimerActive && <span className="otp-time-left">{leftTime}</span>}
            </div>
        </div>
    )
});
